'use client';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import uniqueId from 'lodash/uniqueId';
import moment from 'moment';
import { FC, useCallback, useState } from 'react';

import type { DownloadReport, SetShowSnackbar } from '../../types';

import { formatDateParkerStatus, US_TIMEZONES } from '../../utils';
import { dayjs } from '../../utils/dayjs';
import { RedshiftFacilitySearch } from './redshiftFacilitySearch';
import { ReportActionButton } from './reportActionButton';

interface OutstandingBalancesReportProps {
	setShowSnackbar: SetShowSnackbar;
	downloadReport: DownloadReport;
	forTenant?: boolean;
	prefillAccountId?: string;
	prefillSubName?: string;
}

export const OutstandingBalancesReportRedshiftForm: FC<OutstandingBalancesReportProps> = ({
	setShowSnackbar,
	downloadReport,
	prefillAccountId,
	prefillSubName,
	forTenant = false,
}) => {
	const [filterType, setFilterType] = useState<string>('Account ID');
	const [accountId, setAccountId] = useState<number | null>(null);
	const [subscriptionName, setSubscriptionName] = useState<string>();
	const [asOfDate, setAsOfDate] = useState<string | null>();
	const [currentBalances, setCurrentBalances] = useState<boolean>(true);
	const [isDownloadingPdf, setIsDownloadingPdf] = useState<boolean>(false);
	const [isDownloadingCsv, setIsDownloadingCsv] = useState<boolean>(false);
	const [selectedFacilities, setSelectedFacilities] = useState<any[]>([]);
	const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
	const [selectedDistricts, setSelectedDistricts] = useState<any[]>([]);
	const [selectedBranches, setSelectedBranches] = useState<any[]>([]);
	const [timezone, setTimezone] = useState<string>(dayjs.tz.guess());

	const getDownloadParams = useCallback(() => {
		const tenantCurrentBalances = forTenant ? true : currentBalances;

		const baseParams = {
			accountId,
			subscriptionName,
			...(prefillAccountId && { accountId: prefillAccountId }),
			...(prefillSubName && { subscriptionName: prefillSubName }),
			...(selectedFacilities.length !== 0 && {
				facilities: selectedFacilities.map((facility) => ({
					id: facility.id,
					facilityName: facility.facilityName,
				})),
			}),
			...(selectedAreas.length !== 0 && {
				facilityAreas: selectedAreas.map((area) => area.id),
			}),
			...(selectedBranches.length !== 0 && {
				facilityBranches: selectedBranches.map((branch) => branch.id),
			}),
			...(selectedDistricts.length !== 0 && {
				facilityDistricts: selectedDistricts.map((district) => district.id),
			}),
			timezone,
			currentBalances: tenantCurrentBalances,
			asOfDate: formatDateParkerStatus(asOfDate as string),
		};

		const spreadSheetParams = { currentBalances: tenantCurrentBalances };

		return {
			...baseParams,
			...spreadSheetParams,
		};
	}, [
		accountId,
		subscriptionName,
		asOfDate,
		currentBalances,
		prefillSubName,
		prefillAccountId,
		selectedFacilities,
		timezone,
		selectedAreas,
		selectedBranches,
		selectedDistricts,
	]);

	const handleMultipleFacilityChange = (selections) => {
		setSelectedFacilities(selections);
	};

	const params = getDownloadParams();
	const handleValidation = useCallback(() => {
		const validations = [
			{
				message: `Please select an As of Date.`,
				failingCondition: !asOfDate,
			},
		];
		const failingValidations = validations.filter(({ failingCondition }) => failingCondition);
		if (failingValidations.length) {
			setShowSnackbar({
				message: failingValidations[0].message,
				severity: 'error',
				autoHideDuration: 3000,
				onClose: () => {
					setShowSnackbar(null);
				},
			});
			return false;
		}
		return true;
	}, [setShowSnackbar, asOfDate]);

	const handleReset = useCallback(() => {
		setFilterType('');
		setAccountId(null);
		setAsOfDate(null);
		setCurrentBalances(true);
		setSubscriptionName('');
		setSelectedAreas([]);
		setSelectedBranches([]);
		setSelectedDistricts([]);
		setSelectedFacilities([]);
	}, []);

	return (
		<Box sx={{ padding: 3 }}>
			<Grid container spacing={2}>
				<Grid item xs={4}>
					<FormControl fullWidth variant='outlined'>
						<InputLabel>Filter Options</InputLabel>
						<Select
							label='Filter Options'
							value={filterType || ''}
							onChange={(e) => setFilterType(e.target.value)}
							data-cy='report-type-select-menu'
						>
							{['Account ID', 'Subscription Name']?.map((filter) => (
								<MenuItem key={filter} value={filter}>
									{filter}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
				{filterType === 'Account ID' && (
					<Grid item xs={4}>
						<TextField
							fullWidth
							variant='outlined'
							label='Account ID'
							disabled={forTenant}
							type='number'
							value={accountId || prefillAccountId || ''}
							onChange={(e) => setAccountId(Number(e.target.value))}
							inputProps={{ 'data-cy': 'account-id-input' }}
							onKeyDown={(event) => {
								if (event?.key === '-' || event?.key === '+' || event?.key === 'e') {
									event.preventDefault();
								}
							}}
							onPaste={(event) => {
								event.preventDefault();

								const paste = event.clipboardData.getData('text');
								const cleanedPaste = /^[AC]*[-]*(\d+)$/.exec(paste);

								if (cleanedPaste) {
									setAccountId(Number(cleanedPaste[1]));
								}
							}}
							InputProps={{
								startAdornment: <InputAdornment position='start'>AC-</InputAdornment>,
							}}
						/>
					</Grid>
				)}
				{filterType === 'Subscription Name' && (
					<Grid item xs={4}>
						<TextField
							fullWidth
							variant='outlined'
							label='Sub Name'
							disabled={forTenant}
							value={subscriptionName || prefillSubName || ''}
							onChange={(e) => setSubscriptionName(e.target.value)}
							inputProps={{ 'data-cy': 'sub-name-input' }}
						/>
					</Grid>
				)}
				<Grid item xs={4}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='As of Date *'
							value={asOfDate}
							slotProps={{
								textField: {
									fullWidth: true,
									inputProps: { 'data-cy': 'as-of-date-picker' },
								},
							}}
							onChange={setAsOfDate}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
			<Grid sx={{ marginTop: '15px' }}>
				<RedshiftFacilitySearch
					selectedAreas={selectedAreas}
					setSelectedAreas={setSelectedAreas}
					selectedDistricts={selectedDistricts}
					setSelectedDistricts={setSelectedDistricts}
					selectedBranches={selectedBranches}
					setSelectedBranches={setSelectedBranches}
					selectedFacilities={selectedFacilities}
					setSelectedFacilities={setSelectedFacilities}
					handleMultipleFacilityChange={handleMultipleFacilityChange}
				/>
			</Grid>
			<Grid container spacing={2} marginTop={1}>
				<Grid item xs={4}>
					<FormControl variant='filled' size='small'>
						<Select
							size='small'
							variant='outlined'
							id='timezone'
							value={timezone}
							onChange={(event) => setTimezone(event.target.value)}
							sx={{ height: '36px' }}
						>
							{moment.tz.zonesForCountry('US').map((tz) => (
								<MenuItem key={`${tz}-${uniqueId()}`} value={tz}>
									{tz}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			</Grid>
			<Box display='flex' justifyContent='center' marginTop={3}>
				<Box mr={3}>
					<FormControlLabel
						control={
							<Checkbox
								checked={currentBalances}
								size='small'
								disabled={forTenant}
								onChange={(e) => setCurrentBalances(e.target.checked)}
								data-cy='current-balances-checkbox'
							/>
						}
						label='Current Balances'
					/>
				</Box>
			</Box>

			<ReportActionButton
				downloadReport={(format: string) =>
					downloadReport(format, {
						reportType: 'outstandingBalancesReport' as string,
						params,
						handleValidation,
						setIsDownloadingCsv,
						setIsDownloadingPdf,
					})
				}
				isDownloadingPdf={isDownloadingPdf}
				isDownloadingCsv={isDownloadingCsv}
				onReset={handleReset}
			/>
		</Box>
	);
};
